import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';
import { WarningCircle } from '@phosphor-icons/react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function friendlyLoginError(err) {
  if (!err) return 'Error al iniciar sesión. Intentá de nuevo.';
  const s = String(err).toLowerCase();
  if (s.includes('429') || s.includes('too many'))
    return 'Demasiados intentos. Esperá unos minutos y volvé a probar.';
  if (s.includes('invalid') || s.includes('401') || s.includes('credentials'))
    return 'Usuario o contraseña incorrectos. Verificá los datos.';
  if (s.includes('network') || s.includes('failed to fetch') || s.includes('timeout'))
    return 'No pudimos conectar con el servidor. Revisá tu conexión a internet.';
  return err;
}

export const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMsg('');
    if (!email.trim() || !password) {
      setErrorMsg('Completá usuario/email y contraseña.');
      return;
    }
    setLoading(true);

    try {
      const result = await login(email.trim(), password);
      if (result.success) {
        toast.success('¡Bienvenido de nuevo!');
        try {
          const { data } = await axios.get(`${API_URL}/api/auth/me`, {
            withCredentials: true,
          });
          navigate(data.role === 'admin' ? '/admin' : '/dashboard');
        } catch {
          navigate('/dashboard');
        }
      } else {
        const msg = friendlyLoginError(result.error);
        setErrorMsg(msg);
        toast.error(msg);
      }
    } catch (err) {
      const msg = friendlyLoginError(err.message);
      setErrorMsg(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <h1 className="text-3xl sm:text-4xl font-bold mb-2" data-testid="login-title">
            Iniciar Sesión
          </h1>
          <p className="text-muted-foreground">Bienvenido de vuelta a SMM Panel</p>
        </div>

        <Card className="p-8 border-border">
          <form onSubmit={handleSubmit} className="space-y-6" noValidate>
            {errorMsg && (
              <div
                className="flex items-start gap-2 p-3 rounded-lg bg-destructive/10 border border-destructive/30 text-sm"
                data-testid="login-error-message"
                role="alert"
              >
                <WarningCircle size={18} weight="fill" className="text-destructive shrink-0 mt-0.5" />
                <span>{errorMsg}</span>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="email">Usuario o correo electrónico</Label>
              <Input
                id="email"
                type="text"
                autoComplete="username"
                placeholder="tu_usuario o tu@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                data-testid="login-email-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Contraseña</Label>
              <Input
                id="password"
                type="password"
                autoComplete="current-password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                data-testid="login-password-input"
              />
            </div>

            <div className="flex items-center justify-between">
              <Link
                to="/forgot-password"
                className="text-sm text-primary hover:underline"
                data-testid="forgot-password-link"
              >
                ¿Olvidaste tu contraseña?
              </Link>
            </div>

            <Button
              type="submit"
              className="w-full"
              disabled={loading}
              data-testid="login-submit-button"
            >
              {loading ? 'Iniciando sesión...' : 'Iniciar Sesión'}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-sm text-muted-foreground">
              ¿No tienes cuenta?{' '}
              <Link
                to="/register"
                className="text-primary font-medium hover:underline"
                data-testid="register-link"
              >
                Regístrate aquí
              </Link>
            </p>
          </div>
        </Card>
      </div>
    </div>
  );
};